/** Contrast helpers for rendering tenant brand colours safely.
 *
 * An org's primary colour is arbitrary user input — it can be white,
 * near-black, or the exact shade of the surface it sits on. Anywhere we
 * paint with it we gate on measured contrast (WCAG relative luminance)
 * rather than hoping, so a tenant can never configure themselves an
 * invisible or unreadable control.
 */

/** Parse ``#rgb`` / ``#rrggbb`` into 0-255 channels, or null. */
export function parseHex(hex: string | null | undefined): [number, number, number] | null {
  if (!hex) return null;
  const s = hex.trim().replace(/^#/, "");
  const full =
    s.length === 3
      ? s
          .split("")
          .map((c) => c + c)
          .join("")
      : s;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

/** WCAG relative luminance (0 = black, 1 = white). */
export function relativeLuminance(hex: string): number {
  const rgb = parseHex(hex);
  if (!rgb) return 0;
  const [r, g, b] = rgb.map((c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two colours (1 = identical, 21 = max). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Foreground (black or white) that reads best on ``background``. */
export function onColor(background: string): "#ffffff" | "#111111" {
  return contrastRatio(background, "#ffffff") >=
    contrastRatio(background, "#111111")
    ? "#ffffff"
    : "#111111";
}

/** A swatch of ``color`` on ``surface`` needs an outline when the two
 *  are too close to tell apart — otherwise a white-on-white (or
 *  navy-on-navy) org colour renders as an invisible dot. */
export function needsRing(color: string, surface: string): boolean {
  return contrastRatio(color, surface) < 1.6;
}

/** ``color`` laid over ``base`` at ``amount`` (0 = ``base``, 1 = ``color``),
 *  as an opaque ``#rrggbb`` — a tint whose contrast can be measured,
 *  unlike an alpha that depends on whatever ends up behind it. */
export function mixHex(color: string, base: string, amount: number): string {
  const a = parseHex(color);
  const b = parseHex(base);
  if (!a || !b) return base;
  const t = Math.min(1, Math.max(0, amount));
  return `#${a
    .map((v, i) => Math.round(v * t + (b[i] as number) * (1 - t)).toString(16).padStart(2, "0"))
    .join("")}`;
}
